import React, { useEffect, useState } from 'react'
import {
    Box,
    CircularProgress,
    Modal,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import axios, { AxiosRequestConfig } from 'axios'
import { useMsal } from '@azure/msal-react'
import { InteractionRequiredAuthError, SilentRequest } from '@azure/msal-browser'
import { getAuthConfig } from '../utils/authApi'
import { parseIsoDateTimeAsBerlin } from '../utils/functions/timeUtils'
import { format, parse } from 'date-fns'
import { de, enUS } from 'date-fns/locale'
import { formatInTimeZone } from 'date-fns-tz'

const BERLIN_TIME_ZONE = 'Europe/Berlin'

type Props = {
    open: boolean
    onClose: () => void
}

export default function OperationPeriods({ open, onClose }: Props) {
    const { t, i18n } = useTranslation()
    const { instance, accounts } = useMsal()

    const [loading, setLoading] = useState(false)
    const [periods, setPeriods] = useState<any[]>([])

    const isDe = i18n.language?.startsWith('de')
    const locale = isDe ? de : enUS
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone

    const datePattern = isDe ? 'dd.MM.yyyy' : 'MM/dd/yyyy'
    const monthPattern = isDe ? 'MM.yyyy' : 'MM/yyyy'

    const formatDate = (value: unknown) => {
        const d = parseIsoDateTimeAsBerlin(value)
        return d ? format(d, datePattern) : ''
    }

    const formatExecutionDate = (value: unknown) => {
        const d = parseIsoDateTimeAsBerlin(value)
        if (!d) return ''

        const berlinDayIso = formatInTimeZone(d, BERLIN_TIME_ZONE, 'yyyy-MM-dd')
        const tenAmBerlin = parseIsoDateTimeAsBerlin(`${berlinDayIso}T10:00:00`)
        if (!tenAmBerlin) return format(d, datePattern)

        const datePart = formatInTimeZone(tenAmBerlin, userTimeZone, datePattern)
        const timePart = formatInTimeZone(tenAmBerlin, userTimeZone, 'HH:mm zzz', { locale })
        return `${datePart} ${t('monthlyclosing.at')} ${timePart}`
    }

    const formatMonth = (period: any) => {
        const raw = period?.Month ?? period?.month ?? period?.Period ?? period?.period
        const rawStr = raw !== undefined && raw !== null ? String(raw).trim() : ''

        let d: Date | null = null
        if (rawStr) {
            const parsed = parse(rawStr, monthPattern, new Date())
            if (!Number.isNaN(parsed.getTime())) d = parsed
            if (!d) {
                const fallbackPattern = isDe ? 'M.yyyy' : 'M/yyyy'
                const parsedFallback = parse(rawStr, fallbackPattern, new Date())
                if (!Number.isNaN(parsedFallback.getTime())) d = parsedFallback
            }
        }

        if (!d) d = parseIsoDateTimeAsBerlin(period?.From ?? period?.from)
        return d ? format(d, 'LLLL', { locale }) : ''
    }

    useEffect(() => {
        if (!open) return
        if (!accounts[0]) return

        const accessTokenRequest: SilentRequest = {
            scopes: ['openid', 'profile', 'email', 'user.read'],
            account: accounts[0],
            forceRefresh: true,
        }

        setLoading(true)
        getAuthConfig(instance, accounts[0])
            .then((headers) => {
                const config: AxiosRequestConfig = {
                    ...headers,
                    data: {},
                    params: {
                        date: new Date().toISOString(),
                        filtered: false,
                    },
                }
                return axios.get(`${process.env.REACT_APP_BACKEND_URL}/OperationalPeriods`, config)
            })
            .then((response) => {
                    // only operational periods where Type == 0 --> Monthly closings
                response = {
                    ...response,
                    data: Array.isArray(response.data)
                        ? response.data
                            .filter((p) => p?.Type === 0)
                            .sort((a, b) => {
                                const aFrom = a?.From ?? a?.from
                                const bFrom = b?.From ?? b?.from
                                return new Date(aFrom).getTime() - new Date(bFrom).getTime()
                            })
                        : [],
                }
                setPeriods(Array.isArray(response.data) ? response.data : [])
            })
            .catch((error) => {
                if (error instanceof InteractionRequiredAuthError) {
                    return instance.acquireTokenRedirect(accessTokenRequest)
                }
                console.log(error)
            })
            .finally(() => {
                setLoading(false)
            })
    }, [open, accounts[0]])

    return (
        <Modal open={open} onClose={onClose} aria-labelledby="operational-periods-modal-title" aria-describedby="operational-periods-modal-description">
            <Box
                sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 'calc(100% - 32px)',
                    maxWidth: 900,
                    height: 'calc(100% - 32px)',
                    maxHeight: 'fit-content',
                    overflow: 'auto',
                    bgcolor: 'background.paper',
                    border: 0,
                    borderRadius: 1,
                    boxShadow: 24,
                    p: {
                        xs: 2,
                        sm: 4,
                    },
                }}
            >
                <Typography id="operational-periods-modal-title" variant="h6" component="h2">
                    {t('monthlyClosings.title')}
                </Typography>

                <Box id="operational-periods-modal-description" sx={{ mt: 2 }}>
                    {loading ? (
                        <Stack direction="row" sx={{ justifyContent: 'center', py: 4 }}>
                            <CircularProgress size={28} />
                        </Stack>
                    ) : periods.length === 0 ? (
                        <Typography variant="body2">{t('monthlyClosings.noData')}</Typography>
                    ) : (
                        <TableContainer component={Paper} variant="outlined">
                            <Table size="small" aria-label="operational periods">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>{t('monthlyClosings.columns.month')}</TableCell>
                                        <TableCell>{t('monthlyClosings.columns.fromDate')}</TableCell>
                                        <TableCell>{t('monthlyClosings.columns.toDate')}</TableCell>
                                        <TableCell>{t('monthlyClosings.columns.executionDate')}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {periods.map((p: any, idx: number) => (
                                        <TableRow key={p?.Id ?? p?.id ?? `${p?.Type ?? 'p'}_${idx}`}>
                                            <TableCell>{formatMonth(p)}</TableCell>
                                            <TableCell>{formatDate(p?.From ?? p?.from)}</TableCell>
                                            <TableCell>{formatDate(p?.To ?? p?.to)}</TableCell>
                                            <TableCell>{formatExecutionDate(p?.Effective ?? p?.effective)}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Box>
            </Box>
        </Modal>
    )
}
